import React, {useEffect, useRef} from "react";
import {GetMessageCodeText} from "../app-components/outputs/MessageCodes";
import {CheckOperations, ComparisonOperations} from "../app-components/run-project/Operations";
import {v4} from "uuid";
import HecRasHelper from "../helpers/HecRasHelper";
import {Layers} from "../app-components/map-plugins/LayerSelect";
import {type} from "plotly.js/src/components/errorbars/attributes";
import { debounce } from 'lodash';
export const AppActions = {
    SET_APP_CONTEXT: 'SET_APP_CONTEXT',
    SET_PROJECT: 'SET_PROJECT',
    SET_PROJECTS: 'SET_PROJECTs',
    REMOVE_PROJECT: 'REMOVE_PROJECT',
    SET_HISTORY: 'SET_HISTORY',
    SET_HISTORY_LIST: 'SET_HISTORY_LIST',
    SET_SELECTED_HISTORY_ID : 'SET_SELECTED_HISTORY_ID',
    PUSH_OPERATION_MESSAGE: 'PUSH_OPERATION_MESSAGE',
    SET_TAB: 'SET_TAB',
    SET_SELECTED_OPERATION: 'SET_SELECTED_OPERATION',
    SET_SELECTED_ERROR_ID: 'SET_SELECTED_ERROR_ID',
    SET_SELECTED_RS: 'SET_SELECTED_RS',
    SET_MESSAGE_TOOLBAR_ITEM: 'SET_MESSAGE_TOOLBAR_ITEM',
    SET_PAGE_SETTINGS: 'SET_PAGE_SETTINGS',
    SET_MAP_CONTEXT: 'SET_MAP_CONTEXT',
    SET_MAP_ZOOM: 'SET_MAP_ZOOM',
    SET_MAP_CENTER: 'SET_MAP_CENTER',
    SET_MAP_TYPE: 'SET_MAP_TYPE',
    SET_SHARED_MAP_VIEW: 'SET_SHARED_MAP_VIEW',
    SET_PROJECT_SELECTION: 'SET_PROJECT_SELECTION',
    SET_FILTER: 'SET_FILTER',
    APPLY_PLAN_FILTER: 'APPLY_PLAN_FILTER',
    CLEAR_PLAN_FILTER: 'CLEAR_PLAN_FILTER',
    CLEAR: 'CLEAR'
}

const numberArray = () => {
    const nums = []
    for(let i=1; i<=10000; i++) {
        nums.push(String(i))
    }
    return nums
}
export const ModelNames = numberArray()?.map(x => `Model ${x}`)

export const DefaultPageSettings = {
    sort: 'ds'
}

export const Bounding = {
    northWest: 250,
    northEast: 250,
    southWest: 250,
    southEast: 250,
    showBounding: false
}

export const FlowArrow = {
    flow_count: 5,
    flow_size: 15,
    flow_change_size: 50,
    boundary_size: 50
}

export const TerrainLayer = {
    resolution_size: 256,
    opacity_size: 1,
    gray_opacity_size: 0.5,
    gray_z_factor: 5,
    azimuth: 315,
    altitude: 45,
}

export const LMASettings = {
    id: null,
    gageRS: null,
    minBankElev: null,
    yr100WSEL: null,
    yr500WSEL: null,
    maxHighChord: null,
    minTargetElev: null,
    maxTargetElev: null,
    targetElevInterval: 0.5,
    numberOfTargetProfiles: null,
}

export const LMAData = {
    runKey: null,
    originalValues: {
        ...LMASettings,
    },
    editedValues: {
        ...LMASettings,
    },
    flowTable: [],
    numberOfProfileSearch: 1000,
    flowData: {},
    prepared: false
};

const DefaultValue = {
    page: {...DefaultPageSettings},
    filter: {
        riverStations: [],
        type: 'clear',
        us: '',
        ds: '',
        sort: 'ds'
    },
    filterType: '',
    projects: [],
    // Plans removed by the plan-filter live here (whole projects whose plans
    // were all filtered out), so a Rollback can put everything back.
    filteredOutProjects: [],
    planFilter: {
        active: false,
        profile: null,
        version: 0
    },
    history: [],
    selectedHistoryId: '',
    selectedTab: 'Message',
    selectedOperation: null,
    previousOperation: null,
    selectedErrorId: null,
    selectedRS: null,
    plot: {},
    selectedMapType: 'base',
    map: {
        sharedView: { zoom: null, center: null },
        base: {
            referenceLayers: [],
            projectLayers: [],
            selectedLayers: {
                model: [...Layers.filter(x => !['Flow Change Location', 'Flow Path', 'Boundary Condition'].includes(x))],
                reference: [],
                project: []
            },
            bounding: {...Bounding},
            flowArrow: {
                ...FlowArrow
            },
            terrainLayers: {
                ...TerrainLayer
            },
            zoom: 18,
        },
        compare: {
            referenceLayers: [],
            projectLayers: [],
            selectedLayers: {
                model: [...Layers.filter(x => !['Flow Change Location', 'Flow Path', 'Boundary Condition'].includes(x))],
                reference: [],
                project: []
            },
            bounding: {...Bounding},
            flowArrow: {
                ...FlowArrow
            },
            terrainLayers: {
                ...TerrainLayer
            },
            zoom: 18,
        }
    },
    selection: {
        base: {},
        compare: {}
    },
    toolbarItem: null,
    updateKey: v4()
}

const ProjectContext = React.createContext(JSON.parse(JSON.stringify(DefaultValue)));


const processProject = (action, state) => {
    if(!action?.data?.project?.id)
        action.data.project.id = action?.data?.project?.information?.id;

    const _state = {...state}
    if(action?.data?.map) {
        _state.map[action?.data?.map?.type].update = action.data.map[action?.data?.map?.type].update
    }

    const id = action?.data?.project?.information?.id;
    const existIndex = _state?.projects?.findIndex(x => x.information?.id === id)

    if(existIndex >= 0) {
        _state.projects[existIndex] = {...action.data.project}

        // remove plan or project selection
        if(_state.projects[existIndex].projectFiles?.length === 0) {
            _state.projects = _state?.projects?.filter(x => x.information?.id !== id)
            // Selection holds only id refs, so once the project is dropped
            // the resolver returns nothing. Clear the dangling ref.
            if(state?.selection?.base?.projectId === id) state.selection.base = {}
            if(state?.selection?.compare?.projectId === id) state.selection.compare = {}
        }
        else {
            // Plan removed underneath us? Clear the plan ref so the resolver
            // doesn't keep pointing at a non-existent plan.
            const plans = _state.projects[existIndex]?.projectFiles || []
            if(state?.selection?.base?.projectId === id) {
                const stillThere = plans.find(p => p.id === state?.selection?.base?.planId)
                if(!stillThere) state.selection.base = {...state.selection.base, planId: undefined}
            }
            if(state?.selection?.compare?.projectId === id) {
                const stillThere = plans.find(p => p.id === state?.selection?.compare?.planId)
                if(!stillThere) state.selection.compare = {...state.selection.compare, planId: undefined}
            }
        }
    }
    else {
        const allModels = [...ModelNames]
        const existModels = [..._state.projects, ...(_state.filteredOutProjects || [])].map(prj => prj.modelType)
        const notExistModels = allModels.filter(x => !existModels.includes(x))

        _state.projects.push({
            ...action.data.project,
            modelType: notExistModels[0]
        })
    }

    return {
        ..._state
    }
}

// Does this plan expose the given flow profile? We look at the normalised
// profile codes first (mapProfiles[].target / targetName), then fall back to
// the raw flow-file profile names. "FW" also matches a literal "Floodway".
const planHasFlowProfile = (plan, profileValue) => {
    if(!profileValue)
        return true

    const want = String(profileValue).trim().toUpperCase()
    const aliases = want === 'FW' ? ['FW', 'FLOODWAY', 'F.W.'] : [want]

    const candidates = []
    for (const mp of (plan?.mapProfiles || [])) {
        if(mp?.target != null) candidates.push(mp.target)
        if(mp?.targetName != null) candidates.push(mp.targetName)
        if(mp?.select != null) candidates.push(mp.select)
    }
    for (const p of (plan?.flow?.profiles || [])) {
        if(p != null) candidates.push(p)
    }

    return candidates.some(c => aliases.includes(String(c).trim().toUpperCase()))
}

// Merge every plan that the plan-filter set aside (per-project leftovers and
// fully-removed projects) back into a single projects list, untouched.
const restoreFilteredPlans = (state) => {
    return [...(state?.projects || []), ...(state?.filteredOutProjects || [])].map(p => ({
        ...p,
        projectFiles: [...(p?.projectFiles || []), ...(p?.filteredOutFiles || [])],
        filteredOutFiles: []
    }))
}

// HecRasHelper.filterResult / sortOperationsResults mutate the operations
// tree they receive. We used to deep-clone the entire history (project/plan
// snapshots and all) to defend against that. Now that A1 has stripped
// project/plan out of the selection, the operations subtree is the only
// thing we have to clone — selection, summary, mapBase/mapCompare and wsel
// stay shared by reference. Hot reducer paths (SET_FILTER, SET_PAGE_SETTINGS)
// go through this so the gain compounds across runs.
const applyFilterToHistory = (history, filter, direction) => {
    if (!history) return history
    if (!filter && !direction) return history
    const operations = JSON.parse(JSON.stringify(history.operations || []))
    if(filter) {
        HecRasHelper.filterResult({ operations, filter })
    }
    HecRasHelper.sortOperationsResults({
        direction: direction || history.page?.sort,
        operations,
    })
    return {
        ...history,
        operations,
        updateKey: v4(),
    }
}

function projectReducer(state, action) {
    switch (action.type) {
        case AppActions.CLEAR: {
            return {
                ...JSON.parse(JSON.stringify(DefaultValue))
            }
        }
        case AppActions.SET_APP_CONTEXT: {
            return {
                ...action?.data?.context
            }
        }
        case AppActions.SET_PROJECT: {
            return processProject(action, state)
        }
        case AppActions.SET_PROJECTS: {
            let _state = {...state}
            for (const project of (action?.data?.projects || [])) {
                _state = processProject({data: {project: {...project}}}, state)
            }
            return {
                ..._state
            }
        }
        case AppActions.REMOVE_PROJECT: {
            state.projects = state.projects.filter(x => x.information?.id !== action?.data?.id)
            if(state?.selection?.base?.projectId === action?.data?.id) {
                state.selection.base = {}
            }
            if(state?.selection?.compare?.projectId === action?.data?.id) {
                state.selection.compare = {}
            }

            state.history = state.history.filter(x => {
                if(x.selection) {
                    if(x.selection.base?.projectId === action?.data?.id)
                        return false

                    if(x.selection.compare?.projectId === action?.data?.id)
                        return false
                }

                return true
            })

            if(state.projects.length === 0) {
                state.filter = {
                    riverStations: [],
                    type: 'clear',
                    us: '',
                    ds: ''
                }
            }

            return {
                ...state
            }
        }
        case AppActions.SET_HISTORY: {
            const history = action.data.history.map((x, index) => {
                if(!x.page)
                    x.page = JSON.parse(JSON.stringify(DefaultPageSettings))

                return applyFilterToHistory(x, state.filter)
            })

            return {
                ...state,
                history: [...history]
            }
        }
        case AppActions.PUSH_OPERATION_MESSAGE: {
            const _state = {...state}
            const historyIndex = _state.history.findIndex(x => x.id === action.data?.id)
            if(historyIndex >= 0) {
                _state.history[historyIndex] = applyFilterToHistory(_state.history[historyIndex], _state.filter)
            }
            return {
                ..._state,
                selectedHistoryId: action.data?.id,
                previousOperation: {...(state.selectedOperation || {})},
                selectedOperation: action.data?.operation,
                selectedTab: action.data?.tab
            }
        }
        case AppActions.SET_SELECTED_HISTORY_ID: {
            const _state = {...state}
            const historyIndex = _state.history.findIndex(x => x.id === action.data?.id)
            if(historyIndex >= 0) {
                _state.history[historyIndex] = applyFilterToHistory(_state.history[historyIndex], _state.filter)
            }

            if(action.data.operation) {
                return {
                    ..._state,
                    selectedHistoryId: action.data?.id,
                    previousOperation: {...(state.selectedOperation || {})},
                    selectedOperation: action.data?.operation
                }
            }

            return {
                ..._state,
                selectedHistoryId: action.data?.id
            }
        }
        case AppActions.SET_TAB: {
            return {
                ...state,
                selectedTab: action.data?.tab
            }
        }
        case AppActions.SET_SELECTED_OPERATION: {
            return {
                ...state,
                previousOperation: {...(state.selectedOperation || {})},
                selectedOperation: action.data?.item
            }
        }
        case AppActions.SET_PAGE_SETTINGS: {
            const _state = {...state}

            const historyIndex = _state.history.findIndex(x => x.id === _state.selectedHistoryId)

            if(historyIndex >= 0) {
                const original = _state.history[historyIndex]
                const nextPage = {
                    ...original.page,
                    ...(action.data.page || {})
                }

                // Filter+sort over a fresh operations subtree only — selection,
                // summary, mapBase/mapCompare stay shared.
                const history = applyFilterToHistory(
                    {...original, page: nextPage},
                    _state.filter,
                    nextPage.sort
                )

                for (const operation of history.operations) {
                    const selOperation = operation?.children?.find(x => x?.key === _state?.selectedOperation?.key && x?.key_text === _state?.selectedOperation?.key_text)
                    if(selOperation) {
                        _state.selectedOperation = {...selOperation}
                    }
                }

                _state.history = [
                    ..._state.history.slice(0, historyIndex),
                    history,
                    ..._state.history.slice(historyIndex + 1)
                ]
            }

            return {
                ..._state
            }
        }
        case AppActions.SET_SELECTED_ERROR_ID: {
            return {
                ...state,
                selectedErrorId: action.data?.errorId
            }
        }
        case AppActions.SET_SELECTED_RS: {
            return {
                ...state,
                selectedRS: action.data?.rs
            }
        }
        case AppActions.SET_MESSAGE_TOOLBAR_ITEM: {
            return {
                ...state,
                toolbarItem: action.data?.toolbar
            }
        }
        case AppActions.SET_MAP_CONTEXT: {
            return {
                ...state,
                map: {...action.data?.map}
            }
        }
        case AppActions.SET_MAP_TYPE: {
            return {
                ...state,
                selectedMapType: action.data?.mapType || 'base'
            }
        }
        case AppActions.SET_MAP_ZOOM: {
            const type = action?.data?.type
            return {
                ...state,
                map: {
                    ...state.map,
                    [type]: {
                        ...state.map[type],
                        zoom: action.data?.zoom || 18
                    }
                }
            }
        }
        case AppActions.SET_SHARED_MAP_VIEW: {
            return {
                ...state,
                map: {
                    ...state.map,
                    sharedView: {
                        zoom: action.data?.zoom,
                        center: action.data?.center
                    }
                }
            }
        }
        case AppActions.SET_PROJECT_SELECTION: {
            return {
                ...state,
                selection: {...action.data?.selection}
            }
        }
        case AppActions.SET_FILTER: {
            const _state = {...state}

            _state.filter = {
                ...(_state.filter || {}),
                ...(action.data.filter)
            }

            if(_state.filter.type === 'clear' && (_state.filter.us || _state.filter.ds)) {
                _state.filter.type = 'filter'
            }

            // Apply filter and sort to all histories. Clones only the
            // operations subtree per history (see applyFilterToHistory).
            _state.history = _state.history.map((historyItem) => {
                const direction = _state.filter?.sort || historyItem.page?.sort
                const history = applyFilterToHistory(historyItem, _state.filter, direction)

                if(history.id === _state.selectedHistoryId) {
                    for (const operation of history.operations) {
                        const selOperation = operation?.children?.find(x => x?.key === _state?.selectedOperation?.key && x?.key_text === _state?.selectedOperation?.key_text)
                        if(selOperation) {
                            _state.selectedOperation = {...selOperation}
                        }
                    }
                }

                return history
            })

            return {
                ..._state
            }
        }
        case AppActions.APPLY_PLAN_FILTER: {
            const profile = action?.data?.profile
            if(!profile)
                return {...state}

            // Re-filtering: first pull everything back, then partition again.
            const all = restoreFilteredPlans(state)

            const projects = []
            const filteredOutProjects = []

            for (const project of all) {
                const keep = []
                const removed = []
                for (const pf of (project.projectFiles || [])) {
                    if(planHasFlowProfile(pf, profile))
                        keep.push(pf)
                    else
                        removed.push(pf)
                }

                if(keep.length === 0) {
                    // No matching plan anywhere in this project -> set the whole
                    // project aside (its plans stay in projectFiles for restore).
                    filteredOutProjects.push({...project, filteredOutFiles: []})
                }
                else {
                    projects.push({...project, projectFiles: keep, filteredOutFiles: removed})
                }
            }

            return {
                ...state,
                projects,
                filteredOutProjects,
                planFilter: {
                    active: true,
                    profile,
                    version: (state?.planFilter?.version || 0) + 1
                }
            }
        }
        case AppActions.CLEAR_PLAN_FILTER: {
            return {
                ...state,
                projects: restoreFilteredPlans(state),
                filteredOutProjects: [],
                planFilter: {
                    active: false,
                    profile: null,
                    version: (state?.planFilter?.version || 0) + 1
                }
            }
        }
        default: {
            return {...state}
        }
    }
}

function AppProvider({children}) {
    const [project, dispatch] = React.useReducer(projectReducer, JSON.parse(JSON.stringify(DefaultValue)))

    const value = {project, dispatch}

    return (
        <ProjectContext.Provider value={value}>
            {children}
        </ProjectContext.Provider>
    )
}

function useAppContext() {
    const context = React.useContext(ProjectContext)

    if(!context)
        throw new Error('Project Provider Error')

    const {project: appContext, dispatch} = context;

    const dispatchProject = ({type, data}) => {
        dispatch({
            type,
            data: {...data}
        })
    }

    const getSelectedHistory = () => {
        return appContext?.history?.find(x => x?.id === appContext?.selectedHistoryId) || {operations: []}
    }

    const getOperationByErrorId = () => {
        const selectedOperation = (appContext?.selectedOperation || {})
        return {
            ...selectedOperation,
            data: {
                ...(selectedOperation?.data || {}),
                result: selectedOperation?.data?.result?.filter(x => GetMessageCodeText(selectedOperation?.key, x.error_id) === appContext?.selectedErrorId) || []
            }
        }
    }

    const getResultListByErrorId = () => {
        const selectedOperation = (appContext?.selectedOperation || {})
        return selectedOperation?.data?.result?.find(x => GetMessageCodeText(selectedOperation?.key, x.error_id) === appContext?.selectedErrorId)
    }

    const getCSListByErrorId = () => {
        return getResultListByErrorId()?.data?.map(r => r.cross_section)
    }

    const getMessageCodeList = () => {
        const selectedOperation = (appContext?.selectedOperation || {})
        return selectedOperation?.data?.result?.map(d => GetMessageCodeText(selectedOperation?.key, d?.error_id))
    }

    const getSelectedRS = () => {
        const rs = appContext?.selectedRS?.toString()
            ?.replace('Culv', '')
            ?.replace('BR', '')
            ?.replace('(Sur)', '')
            ?.trim()

        const find = getResultListByErrorId()?.data?.find(x => x.cross_section === rs || x.cross_section_value === rs || x.cross_section === appContext?.selectedRS || x.cross_section_value === appContext?.selectedRS)

        if(find)
            return find

        return {cross_section: rs}
    }

    const getOperationItemConfig = () => {
        for (const checkOperation of [...CheckOperations, ...ComparisonOperations]) {
            for (const child of checkOperation.children) {
                if(child.key === appContext?.selectedOperation?.key) {
                    return child.ui
                }
            }
        }
    }

    const getHistorySorted = () => {
        const _history = [...(appContext?.history || [])]
        _history.sort(function(a, b) {
            // const isimA = a?.selection?.base?.project?.information?.title?.toUpperCase(); // Büyük/küçük harf duyarlı olmayacak şekilde isimleri karşılaştırır
            // const isimB = b?.selection?.base?.project?.information?.title?.toUpperCase();
            // if (isimA < isimB) {
            //     return -1;
            // }
            // if (isimA > isimB) {
            //     return 1;
            // }
            // İsimler aynı ise tarihlerine göre sıralama
            return new Date(a.date) - new Date(b.date);
        });

        return _history
    }

    const getProjectLayers = (type, _selection) => {
        const selection = _selection || getSelectedHistory()?.selection
        if(selection) {
            const ref = selection[type]
            const projectId = ref?.projectId || ref?.project?.information?.id || ref?.project?.id
            const _project = getProjectById(projectId)
            if(_project)
                return _project.shapeFiles || []
        }

        return [];
    }

    const getMapData = (type) => {
        if(type === 'base')
            return getSelectedHistory()?.mapBase

        return getSelectedHistory()?.mapCompare
    }

    const getRiverBoundingCoords = (type, _mapData) => {
        const mapData = _mapData || getMapData(type)
        if(!mapData) {
            return []
        }

        // Build the bounding extent from ALL on-map geometry, not just the river
        // centerline. Cross-section cutlines run perpendicular to the river (so
        // they, not the centerline, set the true channel width) and structures
        // (bridges/culverts) can reach beyond the cross sections — using only
        // reach_xy left those clipped out of the box.
        const coords = []
        const pushXY = (arr) => {
            if (!Array.isArray(arr)) return
            for (const p of arr) {
                if (Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1])) {
                    coords.push(p)
                }
            }
        }

        mapData?.cross_sections?.forEach(cs => pushXY(cs?.xy))
        mapData?.structures?.forEach(st => st?.xy_list?.forEach(item => pushXY(item?.xy)))

        // River reach centerline between the first and last cross section — keeps
        // the box continuous along the reach and acts as a fallback if the
        // cross-section/structure geometry is unavailable.
        const us = mapData?.cross_sections?.[0]
        const ds = mapData?.cross_sections?.[mapData?.cross_sections?.length - 1]
        const reach_xy = mapData?.reach_xy || []
        for (let i = 0; i < reach_xy.length; i++) {
            const inReach = (us?.river_index == null || ds?.river_index == null)
                ? true
                : (i >= us.river_index && i <= ds.river_index)
            if (inReach && Array.isArray(reach_xy[i])) coords.push(reach_xy[i])
        }

        return coords
    }

    const getCSList = () => {
        if(getSelectedHistory()?.summary?.compare) {
            return getSelectedHistory()?.summary?.compare?.cs_list || []
        }

        if(getSelectedHistory()?.summary?.base) {
            return getSelectedHistory()?.summary?.base?.cs_list || []
        }

        return []
    }

    const getDescriptionMap = () => {
        const sel = getSelectedHistory()
        const baseList = sel?.summary?.base?.descriptions || []
        const cmpList = sel?.summary?.compare?.descriptions || []
        const list = [...baseList, ...cmpList]
        const map = {}
        for (const item of list) {
            if (item?.name == null) continue
            const key = String(item.name).trim()
            if (key && item.description && !map[key]) {
                map[key] = String(item.description).replace(/\s*\n\s*/g, ' ').trim()
            }
        }
        if (typeof window !== 'undefined' && window.__descMapLogged !== sel?.id) {
            console.log('[AppContext] getDescriptionMap', {
                selectedHistoryId: sel?.id,
                baseListLen: baseList.length,
                cmpListLen: cmpList.length,
                mapSize: Object.keys(map).length,
                sampleKeys: Object.keys(map).slice(0, 10),
                sampleEntry: Object.keys(map).length ? {[Object.keys(map)[0]]: map[Object.keys(map)[0]]} : null,
                hasSummaryBase: !!sel?.summary?.base,
                hasSummaryCompare: !!sel?.summary?.compare,
            })
            window.__descMapLogged = sel?.id
        }
        return map
    }

    const hasGeoReference = () => {
        // if(runType === 'Check') {
        //     return HecRasHelper.hasGeoReferenced(selection.base.plan?.geometry?.text)
        // }
        // else {
        //     return HecRasHelper.hasGeoReferenced(selection.base.plan?.geometry?.text) && HecRasHelper.isLibraryPlan(selection.compare.plan?.geometry?.text)
        // }
    }

    // ---------------------------------------------------------------------
    // Selection resolvers
    // ---------------------------------------------------------------------
    // The persisted shape of a selection (in appContext.selection and in every
    // history.selection) is `{ projectId, planId, river, survey }`. Heavy
    // project/plan objects are *not* embedded — callers resolve them from the
    // live `appContext.projects` array via these helpers. Older snapshots that
    // still embed full `project`/`plan` are tolerated as a fallback so a
    // partially-migrated state doesn't crash.
    //
    // `resolveSelectionSide(ref)` returns a new object that has the original
    // ref fields plus a `project` and `plan` resolved against the current
    // projects. Run-time code (RunOperations) and display code can keep
    // reading `sel.project` / `sel.plan` as before.

    const getProjectById = (projectId, projects) => {
        if(!projectId) return null
        return (projects || appContext?.projects || []).find(p => p?.id === projectId || p?.information?.id === projectId) || null
    }

    const getPlanById = (projectId, planId, projects) => {
        const project = getProjectById(projectId, projects)
        if(!project || !planId) return null
        const pf = project?.projectFiles?.find(p => p?.id === planId)
        if(pf) return pf
        return project?.filteredOutFiles?.find(p => p?.id === planId) || null
    }

    const resolveSelectionSide = (sideRef, projects) => {
        if(!sideRef) return sideRef
        // Backwards-compat: a side may still embed `project`/`plan` in some
        // call sites we haven't migrated yet (or in older serialised state).
        // Prefer ids; fall back to embedded objects only when no id is given.
        const projectId = sideRef.projectId || sideRef.project?.information?.id || sideRef.project?.id
        const planId = sideRef.planId || sideRef.plan?.id

        const project = getProjectById(projectId, projects) || sideRef.project || null
        const plan = getPlanById(projectId, planId, projects) || sideRef.plan || null

        return {
            ...sideRef,
            projectId,
            planId,
            project,
            plan,
        }
    }

    const resolveSelection = (sel, projects) => {
        if(!sel) return sel
        return {
            ...sel,
            base: resolveSelectionSide(sel.base, projects),
            compare: resolveSelectionSide(sel.compare, projects),
        }
    }

    const resolveHistorySelection = (history) => {
        if(!history?.selection) return history?.selection
        return resolveSelection(history.selection)
    }

    const getSelectedProject = (type, projects) => {
        const ref = appContext?.selection?.[type]
        const projectId = ref?.projectId || ref?.project?.information?.id || ref?.project?.id
        return getProjectById(projectId, projects)
    }

    const getSelectedPlan = (type, project) => {
        const ref = appContext?.selection?.[type]
        const _project = project || getSelectedProject(type)
        const planId = ref?.planId || ref?.plan?.id
        if(!_project || !planId) return null
        return _project?.projectFiles?.find(x => x?.id === planId)
            || _project?.filteredOutFiles?.find(x => x?.id === planId)
            || null
    };

    const getLMAOfSelectedPlan = (type) => {
        return getSelectedPlan(type)?.LMA || {...LMAData}
    }

    const updateLMAOfSelectedPlan = ({ type, data }) => {
        const _project = getSelectedProject(type);
        const _plan = getSelectedPlan(type, _project);

        if (_plan) {
            _plan.LMA = {
                ...getLMAOfSelectedPlan(type),
                ...data,
                id: v4(),
            };

            dispatchProject({
                type: AppActions.SET_PROJECT,
                data: {
                    project: {
                        ..._project,
                    },
                },
            });
        }
    };

    const applyPlanFilter = (profile) => {
        dispatchProject({
            type: AppActions.APPLY_PLAN_FILTER,
            data: {profile}
        })
    }

    const clearPlanFilter = () => {
        dispatchProject({
            type: AppActions.CLEAR_PLAN_FILTER,
            data: {}
        })
    }

    const resetShapeFilters = (filterType) => {
        if(appContext?.projects?.length === 0)
            return

        for (const project of appContext?.projects) {
            for (const shapeFile of project?.shapeFiles) {
                if(shapeFile.hasSurveyData) {
                    if(filterType === 'message') {
                        shapeFile.filters = {...shapeFile.filtersMessage};
                    }
                    else if(filterType === 'automation') {
                        shapeFile.filters = {...shapeFile.filtersSurveyAutomation};
                    }
                }
            }
        }

        dispatchProject({
            type: AppActions.SET_APP_CONTEXT,
            data: {
                context: appContext
            }
        })
    }

    return {
        appContext: appContext,
        filter: appContext?.filter,
        planFilter: appContext?.planFilter,
        selectedOperation: appContext?.selectedOperation,
        selectedErrorId: appContext?.selectedErrorId,
        selectedRS: appContext?.selectedRS,
        selectedTab: appContext?.selectedTab,
        toolbarItem: appContext?.toolbarItem,
        selectedHistoryId: appContext?.selectedHistoryId,
        previousOperation: appContext?.previousOperation,
        mapContext: appContext?.map,
        selection: appContext?.selection,
        getProjectLayers,
        dispatchProject,
        getSelectedHistory,
        getOperationByErrorId,
        getCSListByErrorId,
        getMessageCodeList,
        getSelectedRS,
        getOperationItemConfig,
        getHistorySorted,
        getMapData,
        getRiverBoundingCoords,
        getCSList,
        getDescriptionMap,
        getSelectedProject,
        getSelectedPlan,
        getLMAOfSelectedPlan,
        updateLMAOfSelectedPlan,
        resetShapeFilters,
        applyPlanFilter,
        clearPlanFilter,
        getProjectById,
        getPlanById,
        resolveSelectionSide,
        resolveSelection,
        resolveHistorySelection,
    };
}

export {AppProvider, useAppContext, ProjectContext}